import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withNamespaces, Trans } from 'react-i18next';
import { getDroppedOrSelectedFiles } from 'html5-file-selector';
import { OperateChecker } from 'apps/components';
import { clientStore } from 'apps/client/app';
import { FileLink } from 'apps/repository/views/fileLink';

import {
  InputFileStyled,
  FileInputWrapper,
  ActionsColumn,
} from 'apps/repository/styles';

import { Button, Tooltip } from 'components';

@withNamespaces()
@observer
export class Actions extends React.Component {
  constructor(props) {
    super(props);

    this.replaceFileRef = React.createRef();
  }

  render() {
    const { fileInfo, canContribute, project, t } = this.props;
    const { reference = {} } = fileInfo;
    const fileType = reference.isFolder ? 'folder' : 'file';
    const { canDownload } = project || {};
    const { operateChecker } = clientStore;

    const repoManager = clientStore.context.get('repoAppManager');
    const handleReplaceFileClick = () => {
      this.replaceFileRef.current.click();
    };

    const handleFileReplaced = e => {
      getDroppedOrSelectedFiles(e).then(list => {
        if (list.length) {
          repoManager.AddFiles(list, { immediate: true }, [
            fileInfo.contributionFile.filename,
          ]);
        }
      });
    };

    const actionAllowed =
      !repoManager.contributionReady ||
      repoManager.committing ||
      repoManager.cancelling;

    if (reference && reference.renaming) {
      return (
        <ActionsColumn renaming={reference.renaming} isFlex>
          <ButtonStyled
            data-testid='project-cancel-rename-file'
            text={<Trans>Cancel</Trans>}
            size='xsmall'
            variant='secondary'
            onClick={() => reference.SetRenaming(false)}
            outline
          />
          <ButtonStyled
            disabled={actionAllowed}
            data-testid='project-confirm-rename-file'
            text={<Trans>Rename</Trans>}
            size='xsmall'
            outline
            onClick={() =>
              repoManager.RenameFile(fileInfo, reference.newFilename)
            }
          />
        </ActionsColumn>
      );
    }

    const availableActions = [];

    if (reference) {
      if (canDownload) {
        availableActions.push('download');
      }

      if (
        canContribute &&
        !(reference.opType && reference.opType === 'REMOVE')
      ) {
        availableActions.push('delete', 'rename', 'replace');
      }
    }
    if (canContribute && reference.opType) {
      availableActions.unshift('reset');
    }

    const { content } = project;
    const actionInfos = {
      download: canDownload && {
        getTooltip: ftype => t(`Download this ${ftype}`),
        children: (
          <span data-testid='project-download-file'>
            <FileLink fileInfo={fileInfo} content={content}>
              <Button
                disabled={actionAllowed}
                variant='secondary'
                icon='download'
                ariaLabel='Download file'
                size='xsmall'
                outline
              />
            </FileLink>
          </span>
        ),
      },
      reset: {
        getTooltip: () => t('Reset changes'),
        children: (
          <Button
            disabled={actionAllowed}
            variant='secondary'
            icon='undo'
            ariaLabel='Reset changes'
            data-testid='project-reset-file'
            onClick={() => repoManager.ResetFile(fileInfo)}
            size='xsmall'
            outline
          />
        ),
      },
      delete: {
        getTooltip: ftype =>
          operateChecker.canDeleteIn ? t(`Delete this ${ftype}`) : '',
        children: (
          <OperateChecker type='canDeleteIn'>
            <Button
              disabled={actionAllowed}
              variant='secondary'
              icon='delete'
              data-testid='project-delete-file'
              ariaLabel='Delete file'
              onClick={() => repoManager.DeleteFile(fileInfo)}
              size='xsmall'
              outline
            />
          </OperateChecker>
        ),
      },
      rename: {
        getTooltip: ftype => t(`Rename this ${ftype}`),
        children: (
          <Button
            disabled={actionAllowed}
            variant='secondary'
            icon='edit'
            ariaLabel='Rename file'
            data-testid='project-rename-file'
            onClick={() => reference.SetRenaming(true)}
            size='xsmall'
            outline
          />
        ),
      },
      replace: {
        getTooltip: ftype => t(`Replace this ${ftype}`),
        children: (
          <FileInputWrapper>
            <Button
              disabled={actionAllowed}
              variant='secondary'
              icon='loop'
              ariaLabel='Replace file'
              data-testid='project-replace-file'
              onClick={handleReplaceFileClick}
              size='xsmall'
              outline
            />
            <InputFileStyled
              type='file'
              ref={this.replaceFileRef}
              multiple={false}
              onChange={handleFileReplaced}
            />
          </FileInputWrapper>
        ),
      },
    };

    return (
      <ActionsColumn isFlex isFlexEnd>
        {availableActions.map((action, i) => {
          const info = actionInfos[action];
          const { children } = info;
          // - we disable renames for folders as a temporary measure because
          // the gut code has trouble with renaming large folders (anything
          // >100 files or so)
          if (fileType === 'folder' && action === 'rename') {
            return null;
          }
          return (
            <React.Fragment key={i}>
              <Tooltip delayShow={500} />
              <Wrapper
                key={i}
                data-tip={info.getTooltip(fileType)}
                hidden={
                  (action === 'download' || action === 'replace') &&
                  fileType === 'folder'
                }
              >
                {children}
              </Wrapper>
            </React.Fragment>
          );
        })}
      </ActionsColumn>
    );
  }
}

const Wrapper = styled.div`
  margin-left: ${props => props.marginLeft || '15px'};
  z-index: ${props => props.zIndex || '2'};
  display: ${props => props.hidden && 'none'};
  float: right;
`;

const ButtonStyled = styled(Button)`
  margin-left: ${props => props.theme.spacing.small};
`;

Actions.propTypes = {
  fileInfo: PropTypes.object,
  project: PropTypes.object,
  canContribute: PropTypes.bool,
  t: PropTypes.func,
};
